import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, LogOut, User, Check, ChevronRight, Calendar, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest, formatRelativeTime } from '../../lib/utils';
import { titleForPath } from '../../config/accessControl';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import WeatherWidget from '../widgets/WeatherWidget';
import GlobalSearch from '../GlobalSearch';

export default function Header({ title, onMenuClick }) {
  const { user, logout, isAdmin, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Resolve a page-specific title from the current route. Falls back to the
  // `title` prop passed by Layout, then to "Dashboard". This makes the top
  // header always reflect the page you're actually on (e.g. "Access Control"
  // on /admin/tech-control-panel) instead of the layout's static label.
  const pageTitle = titleForPath(location.pathname, title || 'Dashboard');
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [acceptingAlternative, setAcceptingAlternative] = useState(null);
  const notificationRef = useRef(null);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showNotifications]);

  const fetchNotifications = async () => {
    try {
      // Fetch notifications and unread count
      const [data, countResponse] = await Promise.all([
        apiRequest('/notifications'),
        apiRequest('/notifications/unread-count')
      ]);
      
      // Handle new API response format
      const notifList = data.notifications || data;
      const prevCount = unreadCount;
      const newCount = data.unread_count || countResponse.count || 0;
      
      setNotifications(Array.isArray(notifList) ? notifList.slice(0, 5) : []);
      setUnreadCount(newCount);
      
      // Play sound for new urgent notifications
      if (newCount > prevCount && prevCount > 0) {
        playNotificationSound();
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };
  
  // Sound notification
  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JjoyGfHBkWk9KR0dLUFljcH6LmKGknpWIfGxdT0RAPkBGUF1tfouaqq+voJF/al1PRD48PkRNXG18jZqnr66il4ZzYFBEPDs9Q01cbn+QnKmvrqGUg3BfT0Q+PD5ETVxufI2ap6+uoZWEc2BQRDw7PURNXHaKoK+2taOUhHNgUEQ8Oz1ETVx2iqCvtrWjlIRzYFBEPDs9RE1cbn+QnKmvrqGUg3BfT0Q+PD5ETVxuf5CcqK6toZSDcF9PRD48PkRNXG58jZqnrq6hlIRzYE9EPDw+RE1cdoqgr7a1o5SEc2BQRD08PURNXG58jZqnrq2hlINwX09EPjw+RE1cbn+QnKiurqGUg3BfT0Q+PD5ETVxufI2ap6+uoZWEc2BQRDw7PURNXG58jZqnrq6hlIRzYFBEPDw9RE1cdoqgr7a1o5SEc19PRD48PkRNXHaKoK+2taOUhHNgUEQ8Oz1ETVxufI2ap66toZSDcF9PRD48PkRNXG5/kJyorq6hlINwX09EPjw+RE1cbn+QnKiurqGUg3BfT0Q+PD5E');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000); // Poll every 15s
    
    // Listen for notification updates from other components
    const handleNotificationsUpdated = () => fetchNotifications();
    window.addEventListener('notifications-updated', handleNotificationsUpdated);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-updated', handleNotificationsUpdated);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMarkRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await apiRequest(`/notifications/${notificationId}/read`, { method: 'POST' });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await apiRequest('/notifications/mark-all-read', { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      // Dispatch event to update other components
      window.dispatchEvent(new Event('notifications-updated'));
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark the full group (or single item) as read
    const idsToRead = (notification._groupUnreadIds && notification._groupUnreadIds.length)
      ? notification._groupUnreadIds
      : (!notification.is_read ? [notification.id] : []);

    if (idsToRead.length > 0) {
      try {
        await Promise.all(idsToRead.map(id =>
          apiRequest(`/notifications/${id}/read`, { method: 'POST' })
        ));
        setNotifications(prev => prev.map(n =>
          idsToRead.includes(n.id) ? { ...n, is_read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - idsToRead.length));
      } catch (error) {
        console.error('Failed to mark notification(s) as read');
      }
    }
    setShowNotifications(false);
    
    // Navigate to specific link or default notifications page
    if (notification.link) {
      // For old meeting notifications that only have /book-meeting without params,
      // add the correct tab based on notification type
      if (notification.link === '/book-meeting' && !notification.link.includes('?')) {
        const meetingTabMap = {
          'meeting_request': 'approve',
          'meeting_approved': 'requests',
          'meeting_rejected': 'requests',
          'meeting_cancelled': 'requests',
        };
        const tab = meetingTabMap[notification.type];
        if (tab) {
          navigate(`/book-meeting?tab=${tab}`);
          return;
        }
      }
      navigate(notification.link);
    } else if (notification.url) {
      navigate(notification.url);
    } else {
      // Default navigation based on notification type - only valid routes
      const defaultLinks = {
        'task_assigned': '/tasks',
        'task_completed': '/tasks',
        'task_update': '/tasks',
        'roster_update': '/admin/roster',
        'roster_published': '/admin/roster',
        'roster_submitted': '/admin/roster',
        'shift_swap': '/admin/roster',
        'leave_approved': '/admin/leave',
        'leave_rejected': '/admin/leave',
        'leave_request': '/admin/leave',
        'chat_message': '/chat',
        'new_message': '/chat',
        'mention': '/chat',
        'incident_reported': '/admin/incidents',
        'training_assigned': '/admin/training',
        'daily_report': '/manager/reports',
        'compliance_due': '/inspections',
        'budget_alert': '/franchise/financials',
        'budget_exceeded': '/franchise/financials',
      };
      const defaultPath = defaultLinks[notification.type] || '/notifications';
      navigate(defaultPath);
    }
  };

  // Handle accepting alternative time for declined meetings
  const handleAcceptAlternative = async (notification, e) => {
    e.stopPropagation();
    
    // Extract booking_id from notification metadata or link
    const bookingId = notification.metadata?.booking_id;
    if (!bookingId) {
      toast.error('Unable to find booking information');
      return;
    }
    
    setAcceptingAlternative(bookingId);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/meeting-booking/accept-alternative/${bookingId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Meeting confirmed for ${data.date} at ${data.time}!`);
        // Mark notification as read
        await apiRequest(`/notifications/${notification.id}/read`, { method: 'POST' });
        fetchNotifications();
        setShowNotifications(false);
      } else {
        toast.error(data.detail || 'Failed to accept alternative time');
      }
    } catch (error) {
      console.error('Error accepting alternative:', error);
      toast.error('Failed to accept alternative time');
    } finally {
      setAcceptingAlternative(null);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_assigned': return '📋';
      case 'task_completed': return '✅';
      case 'task_update': return '📝';
      case 'mention': return '💬';
      case 'blocker_reported': return '🚨';
      case 'group_added': return '👥';
      case 'reminder': return '⏰';
      case 'chat_message': return '💬';
      case 'daily_report': return '📊';
      default: return '🔔';
    }
  };

  // WhatsApp-style avatar helpers for message notifications
  const AVATAR_COLORS = [
    'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500',
    'bg-teal-500', 'bg-sky-500', 'bg-indigo-500', 'bg-purple-500',
    'bg-pink-500', 'bg-rose-500'
  ];
  const getAvatarBg = (name) => {
    if (!name) return 'bg-slate-500';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };
  const isMessageType = (t) => ['new_message', 'group_message', 'group_mention', 'new_chat', 'chat_message', 'mention'].includes(t);

  // Group consecutive message notifications from the same conversation/group
  // (keeps newest as representative, stacks siblings, preserves non-message items)
  const groupedNotifications = useMemo(() => {
    const keyOf = (n) => {
      const m = n.metadata || {};
      return m.conversation_id || m.group_id || null;
    };
    const result = [];
    const seen = new Set();
    for (const n of notifications) {
      const key = keyOf(n);
      const isMsg = isMessageType(n.type);
      if (isMsg && key) {
        if (seen.has(key)) continue;
        seen.add(key);
        const siblings = notifications.filter(x => isMessageType(x.type) && keyOf(x) === key);
        const unread = siblings.filter(x => !x.is_read);
        result.push({
          ...n,
          _groupTotal: siblings.length,
          _groupUnread: unread.length,
          _groupIds: siblings.map(x => x.id),
          _groupUnreadIds: unread.map(x => x.id),
        });
      } else {
        result.push({ ...n, _groupTotal: 1, _groupUnread: n.is_read ? 0 : 1, _groupIds: [n.id], _groupUnreadIds: n.is_read ? [] : [n.id] });
      }
    }
    return result;
  }, [notifications]);

  const notificationsPath = user?.role === 'admin' || user?.role === 'manager' ? '/admin/notifications' : '/notifications';

  return (
    <header className="app-header hidden lg:block" data-testid="app-header">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Inter' }}>
            {pageTitle}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          {/* Global Search */}
          <GlobalSearch />
          
          {/* Weather Widget - Global visibility */}
          <WeatherWidget compact={true} />
          
          {/* Notifications Dropdown */}
          <div className="relative" ref={notificationRef}>
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => setShowNotifications(!showNotifications)}
              data-testid="notifications-button"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <>
                  <span className="absolute -top-1 -right-1 flex h-5 w-5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 text-white text-xs font-bold items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  </span>
                </>
              )}
            </Button>

            {/* Notification Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50 max-h-[70vh] overflow-hidden" data-testid="notifications-dropdown">
                <div className="p-3 border-b bg-slate-50 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Notifications</h3>
                    <div className="flex items-center gap-2">
                      {unreadCount > 0 && (
                        <>
                          <button
                            onClick={handleMarkAllRead}
                            className="text-xs text-[#DC2626] hover:text-[#B91C1C] font-medium"
                          >
                            Mark all read
                          </button>
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            {unreadCount} new
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {groupedNotifications.length > 0 ? (
                      groupedNotifications.map((notification) => {
                        const isMsg = isMessageType(notification.type);
                        const meta = notification.metadata || {};
                        const senderName = meta.sender_name || notification.title || '';
                        const initial = (senderName.trim()[0] || '?').toUpperCase();
                        const avatarBg = getAvatarBg(senderName);
                        const groupUnread = notification._groupUnread || 0;
                        const groupTotal = notification._groupTotal || 1;
                        const hasMore = isMsg && groupTotal > 1;
                        const unreadBadge = groupUnread > 1 ? `${groupUnread} new` : 'new';
                        return (
                        <div
                          key={notification.id}
                          className={`p-3 border-b cursor-pointer transition-colors ${
                            groupUnread === 0 ? 'bg-white hover:bg-slate-50' : 'bg-red-50/50 hover:bg-red-50'
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                          data-testid={`notification-item-${notification.id}`}
                        >
                          <div className="flex items-start gap-3">
                            {isMsg ? (
                              <div className="relative flex-shrink-0">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${avatarBg}`}>
                                  {initial}
                                </div>
                                {groupUnread > 1 && (
                                  <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                                    {groupUnread > 9 ? '9+' : groupUnread}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-lg flex-shrink-0 w-10 h-10 flex items-center justify-center">{getNotificationIcon(notification.type)}</span>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={`text-sm font-semibold truncate ${
                                  groupUnread === 0 ? 'text-slate-700' : 'text-slate-900'
                                }`}>
                                  {notification.title}
                                </p>
                                <span className="text-xs text-slate-400 flex-shrink-0 whitespace-nowrap">{formatRelativeTime(notification.created_at)}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2 mt-0.5">
                                <p className={`text-sm line-clamp-1 ${groupUnread === 0 ? 'text-slate-500' : 'text-slate-700 font-medium'}`}>
                                  {notification.message}
                                </p>
                                {groupUnread > 0 ? (
                                  <span className="flex-shrink-0 h-5 px-2 rounded-full bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center whitespace-nowrap">
                                    {unreadBadge}
                                  </span>
                                ) : (
                                  <button
                                    onClick={(e) => handleMarkRead(notification.id, e)}
                                    className="p-1 hover:bg-slate-200 rounded flex-shrink-0"
                                    title="Mark as read"
                                  >
                                    <Check className="h-3 w-3 text-slate-500" />
                                  </button>
                                )}
                              </div>
                              {hasMore && groupUnread <= 1 && (
                                <p className="text-[11px] text-slate-400 mt-0.5">+{groupTotal - 1} earlier message{groupTotal - 1 > 1 ? 's' : ''}</p>
                              )}
                              
                              {/* Quick Accept Alternative button for declined meetings */}
                              {notification.type === 'meeting_rejected' && notification.metadata?.booking_id && (
                                <button
                                  onClick={(e) => handleAcceptAlternative(notification, e)}
                                  disabled={acceptingAlternative === notification.metadata.booking_id}
                                  className="mt-2 flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-md transition-colors disabled:opacity-50"
                                  data-testid={`accept-alternative-${notification.id}`}
                                >
                                  {acceptingAlternative === notification.metadata.booking_id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Calendar className="h-3 w-3" />
                                  )}
                                  Accept Alternative Time
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })
                    ) : (
                      <div className="p-6 text-center text-slate-500">
                        <Bell className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    )}
                  </div>

                  <div className="p-3 border-t bg-slate-50">
                    <Button
                      className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-medium"
                      onClick={() => {
                        setShowNotifications(false);
                        navigate('/notifications');
                      }}
                      data-testid="check-all-notifications-btn"
                    >
                      <Bell className="h-4 w-4 mr-2" />
                      Check All Notifications
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
            )}
          </div>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2"
                data-testid="user-menu-trigger"
              >
                <div className="w-8 h-8 rounded-full bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="hidden md:inline text-sm font-medium">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(user?.role === 'admin' || user?.role === 'manager' ? '/admin/profile' : '/profile')} data-testid="profile-menu-item">
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                <Bell className="mr-2 h-4 w-4" />
                Notifications &amp; Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout} 
                className="text-red-600 focus:text-red-600"
                data-testid="logout-menu-item"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
